/**
 * Functions that return or update core model or app data only.
 */

import { getAPIData, apiRequest } from "./axios";
import { API_PATHS } from "../../config/apiConfig";
import { isSupportedLanguage } from "../../config/constants";

const getCache = new Map();

/**
 * Deduplicate stable bootstrap GET calls made by multiple contexts during app mount.
 *
 * @param {string} key
 * @param {() => Promise<unknown>} loader
 * @param {boolean} [force]
 * @returns {Promise<unknown>}
 */
function cachedGet(key, loader, force = false) {
  if (force) {
    getCache.delete(key);
  }
  if (!getCache.has(key)) {
    getCache.set(
      key,
      loader().catch((error) => {
        getCache.delete(key);
        throw error;
      })
    );
  }
  return getCache.get(key);
}

/**
 * Backend base URL and DEBUG flag used to build links and feature gates.
 *
 * @returns {Promise<Object>}
 */
export async function getBaseURL() {
  const url = API_PATHS.CORE.BASE_URL;
  return await cachedGet(url, () => getAPIData(url));
}

/**
 * Fetch one user by primary key.
 *
 * @param {number|string} userID CustomUser PK.
 * @returns {Promise<Object>}
 */
export async function getUser(userID) {
  const url = `/core/getuser/${userID}`;
  return await getAPIData(url);
}

/**
 * Session current-user payload. Cached across mount unless `force` is true.
 *
 * @param {Object} [options]
 * @param {boolean} [options.force=false] Bypass the in-memory GET cache.
 * @returns {Promise<Object>}
 */
export async function getCurrentUser({ force = false } = {}) {
  const url = API_PATHS.CORE.CURRENT_USER;
  return await cachedGet(url, () => getAPIData(url), force);
}

/**
 * JobNumber catalog for pickers. Cached; `active` uses the date-window rules.
 *
 * @param {boolean} [activeOnly=true] Restrict to jobs active on `targetDate`.
 * @param {Date|string|null} [targetDate=null] Optional YYYY-MM-DD or Date.
 * @returns {Promise<Array>}
 */
export async function getJobNumbers(activeOnly = true, targetDate = null) {
  const params = new URLSearchParams();
  params.append('active', activeOnly);
  if (targetDate) {
    // Format date as YYYY-MM-DD
    const dateStr = typeof targetDate === 'string' 
      ? targetDate 
      : targetDate.toISOString().split('T')[0];
    params.append('date', dateStr);
  }
  const url = `${API_PATHS.CORE.JOB_NUMBERS}?${params.toString()}`;
  return await cachedGet(url, () => getAPIData(url));
}

/**
 * Active work-scope catalog. Cached across mount.
 *
 * @returns {Promise<Array>}
 */
export async function getScopes() {
  const url = API_PATHS.CORE.SCOPES;
  return await cachedGet(url, () => getAPIData(url));
}

/**
 * Drop the cached work-scope catalog so the next `getScopes` call hits the server.
 *
 * @returns {void}
 */
export function invalidateScopesCache() {
  getCache.delete(API_PATHS.CORE.SCOPES);
}

/**
 * Get active addresses for project/job editing.
 *
 * @returns {Promise<Array>} Array of address objects.
 */
export async function getAddresses() {
  const url = API_PATHS.CORE.ADDRESSES;
  return await cachedGet(url, () => getAPIData(url));
}

/**
 * Get all customers for project/job editing.
 *
 * @returns {Promise<Array>} Array of customer objects.
 */
export async function getCustomers() {
  const url = API_PATHS.CORE.CUSTOMERS;
  return await cachedGet(url, () => getAPIData(url));
}

/**
 * Patch approved fields on a JobNumber record.
 *
 * @param {string|number} jobId - JobNumber primary key.
 * @param {Object} payload - Partial JobNumber update payload.
 * @returns {Promise<Object>} Updated job response.
 */
export async function patchJobNumber(jobId, payload) {
  return await apiRequest(API_PATHS.CORE.JOB_NUMBER_DETAIL(jobId), payload, {
    method: "PATCH",
  });
}

/**
 * Get users filtered by role and/or active status.
 * 
 * @param {Object} params - Filter parameters
 * @param {string} [params.role] - Filter by role ('manager', 'crew', 'staff', 'executive', 'admin')
 * @param {boolean} [params.active] - Filter by active status (true/false)
 * @returns {Promise<Array>} Array of user objects
 * 
 * @example
 * // Get all active managers
 * const managers = await getUsersByRole({ role: 'manager', active: true });
 * 
 * // Get all active users (worker catalogs / pickers)
 * const workers = await getUsersByRole({ active: true });
 * 
 * // Get all users
 * const allUsers = await getUsersByRole();
 */
export async function getUsersByRole({ role = null, active = null } = {}) {
  const params = new URLSearchParams();
  if (role) params.append('role', role);
  if (active !== null) params.append('active', active.toString());
  
  const url = `${API_PATHS.CORE.USERS}${params.toString() ? `?${params.toString()}` : ''}`;
  return await getAPIData(url);
}

/**
 * Upload/process an image payload through the files handle-image endpoint.
 *
 * @param {Object} payload Image handler payload.
 * @returns {Promise<Object>}
 */
export async function handleImage(payload) {
  const url = API_PATHS.FILES.HANDLE_IMAGE;
  const body = { payload };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Handles file uploads to Egnyte by sending them to the backend API for processing.
 *
 * Each request should have a custom action and additional data to be sent with the
 * files. This custom action must be supported by the backend API. If it isn't already,
 * add it to the API.
 *
 * All requests must have files & action as a minimum. Additional data is optional and
 * only used by custom actions.
 *
 * @param {Object} params - The parameters for handling files.
 * @param {Array} params.files - The files to be uploaded.
 * @param {Object} params.files[].file - The file object containing the output method.
 * @param {string} params.files[].filename - The name of the file.
 * @param {string} params.files[].type - The type of the file (e.g., 'pdf', 'image').
 * @param {string} params.action - The action to be performed on the files.
 * @param {Object} params.additional_data - Additional data to be sent with the files.
 * @returns {Promise<Object>} The response from the server.
 */
export const handleFiles = async ({ files, action, additional_data }) => {
  const formData = new FormData();
  const url = API_PATHS.FILES.HANDLE_FILE;

  files.forEach((file, index) => {
    if (file.type === "image" || file.type === "video") {
      formData.append(`file-${index}`, file.file, file.filename);
    } else {
      // For PDFs and other file types:
      // If file has an output method (jsPDF object), use it
      // Otherwise, use the file directly (native File/Blob object)
      if (file.file && typeof file.file.output === "function") {
        const pdfBlob = file.file.output("blob");
        formData.append(`file-${index}`, pdfBlob, file.filename);
      } else {
        // Native File object - use directly
        formData.append(`file-${index}`, file.file, file.filename);
      }
    }
  });
  formData.append("file_count", files.length);
  formData.append("action", action);
  Object.keys(additional_data).forEach((key) => {
    formData.append(key, additional_data[key]);
  });
  // formData.append("additional_data", JSON.stringify(additional_data));

  // Use longer timeout for file uploads (5 minutes) to support large videos
  try {
    const result = await apiRequest(url, formData, {
      method: "POST",
      timeout: 300000  // 5 minutes in milliseconds
    });
    console.log('[handleFiles] Upload successful:', result);
    return result;
  } catch (error) {
    console.error('[handleFiles] Upload failed:', error);
    console.error('[handleFiles] Error response:', error.response);
    console.error('[handleFiles] Error config:', error.config);
    throw error;
  }
};

/**
 * Updates the current user's SMS phone number
 * @param {string} phoneNumber - Phone number in format 123-456-7890
 * @returns {Promise<Object>} Response with success status and updated number
 */
export async function updateSMSNumber(phoneNumber) {
  const url = API_PATHS.CORE.SMS_UPDATE_NUMBER;
  const body = { phone_number: phoneNumber };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Updates the current user's SMS permission
 * @param {string} permission - Either "accepted" or "rejected"
 * @returns {Promise<Object>} Response with success status and permission ID
 */
export async function updateSMSPermission(permission) {
  const url = API_PATHS.CORE.SMS_UPDATE_PERMISSION;
  const body = { permission };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Starts phone login by requesting a Twilio Verify SMS code.
 * @param {string} phoneNumber - Phone number in format 123-456-7890.
 * @returns {Promise<Object>} Generic response for the phone login start request.
 */
export async function startPhoneLogin(phoneNumber) {
  const url = API_PATHS.CORE.AUTH_PHONE_START;
  const body = { phone_number: phoneNumber };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Completes phone login by checking the submitted SMS code.
 * @param {string} phoneNumber - Phone number in format 123-456-7890.
 * @param {string} code - One-time SMS login code.
 * @param {string} next - Redirect target after successful login.
 * @param {string|null} [preferredLanguage] Language to persist on `/login/<code>` only.
 * @returns {Promise<Object>} Response containing the post-login redirect target.
 */
export async function completePhoneLogin(
  phoneNumber,
  code,
  next,
  preferredLanguage = null
) {
  const url = API_PATHS.CORE.AUTH_PHONE_COMPLETE;
  const body = { phone_number: phoneNumber, code, next };
  if (preferredLanguage) {
    body.preferred_language = preferredLanguage;
  }
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Starts executive user impersonation.
 * @param {number} targetUserId - User ID to impersonate.
 * @returns {Promise<Object>} Response containing user and impersonation metadata.
 */
export async function startImpersonation(targetUserId) {
  const url = API_PATHS.CORE.IMPERSONATION_START;
  const body = { target_user_id: targetUserId };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Stops the current executive user impersonation session.
 * @returns {Promise<Object>} Response containing restored user metadata.
 */
export async function stopImpersonation() {
  const url = API_PATHS.CORE.IMPERSONATION_STOP;
  return await apiRequest(url, {}, { method: "POST" });
}

/**
 * DEPRECATED, unused, not maintained.
 * Previously changed the current user's password from the Home password modal.
 * @param {string} oldPassword - Current password
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - New password confirmation
 * @returns {Promise<Object>} Response with success status and message
 */
export async function changePassword(oldPassword, newPassword, confirmPassword) {
  const url = API_PATHS.CORE.CHANGE_PASSWORD;
  const body = {
    old_password: oldPassword,
    new_password: newPassword,
    confirm_password: confirmPassword
  };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * DEPRECATED, unused, not maintained.
 * Previously dismissed the password prompt by setting personal_password true.
 * @returns {Promise<Object>} Response with success status
 */
export async function dismissPasswordPrompt() {
  const url = API_PATHS.CORE.DISMISS_PASSWORD_PROMPT;
  return await apiRequest(url, {}, { method: "POST" });
}

/**
 * Marks the Home new-user popup as completed for the current user.
 *
 * @returns {Promise<Object>} Response with success status and updated flag.
 */
export async function completeNewUserPrompt() {
  const url = API_PATHS.CORE.NEW_USER_PROMPT_COMPLETE;
  return await apiRequest(url, {}, { method: "POST" });
}

/**
 * Updates the current user's preferred language.
 *
 * @param {string} preferredLanguage - Preferred-language code.
 * @returns {Promise<Object>} Response with success status and saved language.
 */
export async function updatePreferredLanguage(preferredLanguage) {
  const url = API_PATHS.CORE.PREFERRED_LANGUAGE;
  const body = { preferred_language: preferredLanguage };
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Translate text through Django (Cloud Translation Basic v2).
 * Callers must never write the result into `workDescription` / Event.description.
 * Omit `source` so Google detects the language on the same call.
 *
 * @param {string} text Source text.
 * @param {string} [source] Source language code. Omitted when unset.
 * @param {string} target Target language code.
 * @returns {Promise<{success: boolean, translatedText?: string, detectedSourceLanguage?: string, error?: string}>}
 */
export async function translateText(text, source, target) {
  const body = { text, target };
  if (isSupportedLanguage(source)) {
    body.source = source;
  }
  return await apiRequest(API_PATHS.CORE.TRANSLATE, body, { method: "POST" });
}

/**
 * Detect language through Django (Cloud Translation Basic v2 detect).
 * Callers must never persist the result into `workDescription`.
 *
 * @param {string|string[]} texts One string or a list of strings.
 * @returns {Promise<{success: boolean, language?: string, languages?: string[], error?: string}>}
 */
export async function detectLanguage(texts) {
  const list = (Array.isArray(texts) ? texts : [texts]).map((item) =>
    item == null ? "" : String(item)
  );
  return await apiRequest(
    API_PATHS.CORE.DETECT_LANGUAGE,
    { texts: list },
    { method: "POST" }
  );
}

/**
 * Fetch the latest unseen announcement for the session user.
 * The backend marks the user as seen when it returns a notice.
 *
 * @returns {Promise<{announcement: {id: number, title: string, body: string}|null}>}
 */
export async function getLatestAnnouncement() {
  return await apiRequest(API_PATHS.CORE.ANNOUNCEMENT, null, { method: "GET" });
}

/**
 * Create an AppAnnouncement. Executive-only on the backend.
 *
 * @param {Object} payload Create form values.
 * @param {string} payload.title Headline.
 * @param {string} payload.body Plain-text body.
 * @param {string|null} [payload.expires] Optional `YYYY-MM-DDTHH:MM` expiry.
 * @param {string} [payload.icon] Icon key (`rs`, `circle`, `triangle`, `clock`, `calendar`).
 * @param {string[]} payload.show_to Role keys that should see the notice (crew, manager, admin, executive).
 * @returns {Promise<Object>} Create response.
 */
export async function createAnnouncement(payload) {
  return await apiRequest(API_PATHS.CORE.ANNOUNCEMENT, payload, {
    method: "POST",
  });
}

/**
 * Fetch enabled broadcast SMS templates (title + body).
 *
 * @returns {Promise<Array<{id: number, title: string, body: string}>>}
 */
export async function getSmsTemplates() {
  return await apiRequest(API_PATHS.CORE.SMS_TEMPLATES, null, { method: "GET" });
}
